import type { SentimentTrendGranularity } from '@up2stay/up2promo-api-client';

const MAX_AXIS_TICKS = 7;

/**
 * Pick evenly spaced indices for x-axis labels (always includes first and last).
 * Used with Recharts `ticks` so only selected buckets render — avoids label spam.
 */
export function buildTrendTickIndices(
  total: number,
  maxTicks = MAX_AXIS_TICKS,
): number[] {
  if (total <= 0) return [];
  if (total <= maxTicks) {
    return Array.from({ length: total }, (_, index) => index);
  }

  const indices = new Set<number>([0, total - 1]);
  const innerSlots = maxTicks - 2;
  const step = (total - 1) / (innerSlots + 1);

  for (let slot = 1; slot <= innerSlots; slot += 1) {
    indices.add(Math.round(step * slot));
  }

  return [...indices].sort((a, b) => a - b);
}

export function buildTrendAxisTicks<T extends { label: string }>(
  data: T[],
  maxTicks = MAX_AXIS_TICKS,
): string[] {
  return buildTrendTickIndices(data.length, maxTicks).map(
    (index) => data[index]?.label ?? '',
  );
}

export function trendAxisInterval(
  total: number,
  granularity: SentimentTrendGranularity,
  maxTicks = MAX_AXIS_TICKS,
): number | 'preserveStartEnd' {
  if (total <= maxTicks) return 0;
  if (granularity === 'month' && total > maxTicks) {
    return Math.max(1, Math.floor(total / maxTicks));
  }
  return 'preserveStartEnd';
}
